"use client";

import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { buscarJsonComCache, invalidarCacheCliente, observarInvalidacaoCacheCliente } from "@/lib/fetch-cache-cliente";
import { resolverExperienciaProduto, type ExperienciaProduto } from "@/lib/experiencia-produto";
import { WEB_BETA } from "@/lib/ambiente-cliente";
import type { ModuloFollowProUsuario } from "@/lib/followpro-acesso-valores";

export type MePublico = Pick<ExperienciaProduto, "produto" | "marca" | "recursos" | "classificacao" | "pipeline" | "etiquetas"> & {
  id: string;
  nome: string;
  papel: "PROGRAMADOR" | "PLATFORM_ADMIN" | "ADM" | "GESTOR" | "USUARIO";
  modulosFollowProUsuario?: readonly ModuloFollowProUsuario[];
};

// Identidade de contexto apenas para os caches clientes, sem persistência.
const geracoesContexto = new WeakMap<object, number>();
let geracaoContexto = 0;
export function contextoExperienciaValido(dados: object) {
  return geracoesContexto.get(dados) === geracaoContexto;
}

function normalizarMePublico(resposta: MePublico | null | undefined) {
  if (!resposta || typeof resposta.id !== "string" || !resposta.id || !resposta.produto || !resposta.marca?.nome || !resposta.marca?.sigla || !resposta.recursos) return null;
  return resposta;
}

const EXPERIENCIA_COMPATIBILIDADE = resolverExperienciaProduto(
  "FOLLOW_CUSTOM",
  "BP_ACOMPANHAMENTO",
  WEB_BETA ? "WEB_BETA" : "WEB_OFICIAL",
);

const ExperienciaContext = createContext<{ dados: MePublico | null; experiencia: ExperienciaProduto; experienciaConfirmada: boolean; carregado: boolean; recarregar: () => Promise<void> }>({
  dados: null,
  experiencia: EXPERIENCIA_COMPATIBILIDADE,
  experienciaConfirmada: false,
  carregado: false,
  recarregar: async () => {},
});

export function ExperienciaProvider({ children }: { children: React.ReactNode }) {
  const [dados, setDados] = useState<MePublico | null>(null);
  const [carregado, setCarregado] = useState(false);
  const epoch = useRef(0);

  const carregar = useCallback(async (forcar = false) => {
    const rodada = ++epoch.current;
    setDados(null);
    if (forcar) invalidarCacheCliente("/api/me");
    try {
      // Um contexto novo não pode vir de cache/pedido iniciado antes do logout.
      const resposta = await buscarJsonComCache<MePublico>("/api/me", { ttl: 5 * 60_000, force: true });
      if (epoch.current === rodada) {
        const contexto = normalizarMePublico(resposta);
        if (contexto) geracoesContexto.set(contexto, geracaoContexto);
        setDados(contexto);
      }
    } catch (erro) {
      if (epoch.current === rodada) setDados(null);
      throw erro;
    } finally {
      if (epoch.current === rodada) setCarregado(true);
    }
  }, []);

  useEffect(() => {
    const removerInvalidacao = observarInvalidacaoCacheCliente((prefixo) => {
      if (prefixo) return;
      geracaoContexto += 1;
      epoch.current += 1;
      setDados(null);
      setCarregado(true);
    });
    const autenticar = () => { void carregar(true).catch(() => {}); };
    window.addEventListener("bp-auth-role-change", autenticar);
    void carregar().catch(() => {});
    return () => {
      epoch.current += 1;
      removerInvalidacao();
      window.removeEventListener("bp-auth-role-change", autenticar);
    };
  }, [carregar]);

  const recarregar = useCallback(async () => carregar(true), [carregar]);

  const experiencia = useMemo<ExperienciaProduto>(() => dados ? {
      produto: dados.produto,
      implementacao: null,
      canal: WEB_BETA ? "WEB_BETA" : "WEB_OFICIAL",
      marca: dados.marca,
      recursos: dados.recursos,
      classificacao: dados.classificacao,
      pipeline: dados.pipeline,
      etiquetas: dados.etiquetas,
    } : EXPERIENCIA_COMPATIBILIDADE, [dados]);
  const experienciaConfirmada = carregado && dados !== null;

  return <ExperienciaContext value={{ dados, experiencia, experienciaConfirmada, carregado, recarregar }}>{children}</ExperienciaContext>;
}

export function useExperienciaProduto() {
  return useContext(ExperienciaContext);
}
